import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, Stack, TextField, Typography,
  Collapse, Paper, Switch, FormControlLabel
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import ConsoleTable from "../Framework/ConsoleTable";
import useConsoleData from "../Data/useConsoleData";
import journalsPlugin from "../Plugins/journals.plugin";
import DialogJournalUpsert from "../../Dialogs/DialogJournalUpsert";
import { useToast } from "../Framework/ToastProvider";

/** ---- 環境変数（Vite / CRA 両対応） ---- */
const env = {
  url:
    (import.meta && import.meta.env && import.meta.env.VITE_SUPABASE_URL) ||
    process.env.REACT_APP_SUPABASE_URL,
  key:
    (import.meta && import.meta.env && import.meta.env.VITE_SUPABASE_ANON_KEY) ||
    process.env.REACT_APP_SUPABASE_ANON_KEY,
};

/** REST を 1 回だけ直叩きして、PostgREST の生レスポンスを確認 */
async function probePostgrestOnce({ table, select, order }) {
  if (!env.url || !env.key) {
    return { ok: false, note: "Supabase URL/Anon Key が未設定です。" };
  }
  const qs = new URLSearchParams();
  qs.set("select", select);
  if (order?.field) qs.set("order", `${order.field}.${order.sort || "asc"}`);
  qs.set("limit", "1");

  const url = `${env.url}/rest/v1/${table}?${qs.toString()}`;
  const res = await fetch(url, {
    headers: {
      apikey: env.key,
      Authorization: `Bearer ${env.key}`,
      Prefer: "count=exact",
    },
  });

  const text = await res.text();
  let json = null;
  try { json = text ? JSON.parse(text) : null; } catch {}
  return {
    ok: res.ok,
    status: res.status,
    url,
    body: json ?? text,
    keys: Array.isArray(json) && json.length ? Object.keys(json[0]) : [],
  };
}

export default function JournalsConsole() {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortModel, setSortModel] = useState([
    { field: journalsPlugin.defaultSort.field, sort: journalsPlugin.defaultSort.sort },
  ]);
  const [search, setSearch] = useState("");

  const [openAdd, setOpenAdd] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const toast = useToast();

  const [debugOpen, setDebugOpen] = useState(false);
  const [probe, setProbe] = useState(null);
  const [bypass, setBypass] = useState(false); // ← ConsoleTable を素通りするスイッチ

  const { rows, total, loading, error } = useConsoleData({
    plugin: journalsPlugin,
    page,
    pageSize,
    sortModel,
    search,
    refreshKey,
    debug: true,
  });

  const columns = useMemo(
    () => journalsPlugin.columns({ onEdit: (row) => setEditRow(row) }),
    []
  );

  useEffect(() => {
    (async () => {
      const order = sortModel?.[0] || journalsPlugin.defaultSort;
      const result = await probePostgrestOnce({
        table: journalsPlugin.table,
        select: journalsPlugin.select,
        order,
      });
      setProbe(result);
      console.groupCollapsed(
        "[Journals/Probe] REST 直叩き",
        result.ok ? "OK" : "NG",
        result.status || ""
      );
      console.log("URL:", result.url);
      console.log("Keys(1行目):", result.keys);
      console.log("Body(sample):", result.body);
      console.groupEnd();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortModel]);

  useEffect(() => {
    console.groupCollapsed(
      "[Journals] useConsoleData result",
      `rows=${rows?.length ?? 0}`,
      `total=${total ?? "?"}`,
      loading ? "loading..." : "loaded"
    );
    if (error) console.error("error:", error);
    console.log("first row(raw):", rows?.[0]);
    console.groupEnd();
  }, [rows, total, loading, error]);

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ flex: 1 }}>Journals</Typography>
        <TextField
          size="small"
          placeholder="Search name / ISSN / publisher"
          value={search}
          onChange={(e) => { setPage(0); setSearch(e.target.value); }}
          sx={{ minWidth: 360 }}
        />
        <FormControlLabel
          control={<Switch checked={bypass} onChange={(e) => setBypass(e.target.checked)} />}
          label="Bypass ConsoleTable"
        />
        <Button variant="outlined" onClick={() => setDebugOpen((v) => !v)} title="診断パネルの表示/非表示">
          Debug
        </Button>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setOpenAdd(true)}>
          Add
        </Button>
      </Stack>

      {/* ---- 診断パネル ---- */}
      <Collapse in={debugOpen} timeout="auto" unmountOnExit>
        <Paper variant="outlined" sx={{ mb: 2, p: 2, fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
          <Typography variant="subtitle1" gutterBottom>Probe（REST 直叩きの結果）</Typography>
          <div>select: <code>{journalsPlugin.select}</code></div>
          <div>table : <code>{journalsPlugin.table}</code></div>
          <div>status: <code>{probe?.status ?? "-"}</code></div>
          <div style={{ marginTop: 6 }}>
            keys(1st row): <code>{Array.isArray(probe?.keys) ? probe.keys.join(", ") : "-"}</code>
          </div>
          {error && <div style={{ color: "#ff5252", marginTop: 6 }}>useConsoleData error: {String(error?.message || error)}</div>}
          <pre style={{ overflow: "auto", marginTop: 10 }}>
            {JSON.stringify(
              {
                url: probe?.url,
                status: probe?.status,
                keys: probe?.keys,
                bodySample: Array.isArray(probe?.body) && probe.body.length ? probe.body[0] : probe?.body,
                firstRowFromHook: rows?.[0] ?? null,
              },
              null,
              2
            )}
          </pre>
        </Paper>
      </Collapse>

      {/* --- 表示部：バイパスで ConsoleTable を疑う --- */}
      {bypass ? (
        <div style={{ height: 640, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            getRowId={(r) => r.id}  // UUID
            pagination
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize, page } } }}
            onPaginationModelChange={(m) => { setPage(m.page); setPageSize(m.pageSize); }}
            disableRowSelectionOnClick
          />
        </div>
      ) : (
        <ConsoleTable
          columns={columns}
          rows={rows}
          total={total}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(s) => { setPage(0); setPageSize(s); }}
          sortModel={sortModel}
          onSortModelChange={(m) => { setPage(0); setSortModel(m); }}
          onRowClick={(row) => setEditRow(row)}
          loading={loading}
        />
      )}

      {openAdd && (
        <DialogJournalUpsert
          open
          mode="add"
          onClose={(saved) => {
            setOpenAdd(false);
            if (saved) {
              toast.push("success", "Journal created");
              setRefreshKey((k) => k + 1);
            }
          }}
        />
      )}

      {editRow && (
        <DialogJournalUpsert
          open
          mode="edit"
          journal={editRow}
          onClose={(saved) => {
            setEditRow(null);
            if (saved) {
              toast.push("success", "Journal updated");
              setRefreshKey((k) => k + 1);
            }
          }}
        />
      )}
    </Box>
  );
}
